'use strict';

/**
 * Project edit access: may this user change a Project record from the config app (and the micro apps built on it)?
 *
 * Mirrors the server. The config app sends no x-application-name on Project writes, so the API decides a Project
 * update with the legacy Meadow rule in api/headlight/source/modules/schemas/MeadowSchema-Project.json (the
 * top-level Authorization block), not the permissions engine:
 *   - level 5 (Administrator): Allow;
 *   - level 4 (Executive): MyCustomer, the project is in the user's customer;
 *   - levels 1-3 (User, Manager, Director): Mine, the user created the project (Project.CreatingIDUser);
 *   - level 0 (Readonly): Deny.
 * Keep the two in step. The server stays the final authority; this only stops the screens offering a save it refuses.
 *
 * Pure functions, no pict / network, so they test in isolation.
 */

/**
 * @typedef {object} ProjectEditUser
 * @property {number|string} [UserID]
 * @property {number|string} [UserRoleIndex] - the user level (0-5)
 * @property {number|string} [CustomerID]
 */

/**
 * @typedef {object} ProjectEditRecord
 * @property {number|string} [IDCustomer]
 * @property {number|string} [CreatingIDUser]
 */

/**
 * Is this user an administrator of the project: Headlight level 5, or level 4 in the project's own customer?
 *
 * @param {ProjectEditUser} pUser - pict.AppData.CurrentUser
 * @param {ProjectEditRecord} pProject - the loaded Project record
 * @return {boolean}
 */
function isProjectAdministrator(pUser, pProject)
{
	const tmpUser = pUser || {};
	const tmpLevel = Number(tmpUser.UserRoleIndex);
	if (tmpLevel >= 5)
	{
		return true;
	}
	if (!pProject)
	{
		return false;
	}
	return (tmpLevel === 4) && (Number(pProject.IDCustomer) === Number(tmpUser.CustomerID));
}

/**
 * May this user save changes to the Project record? A project administrator, or a level 1-3 user who created it.
 *
 * @param {ProjectEditUser} pUser - pict.AppData.CurrentUser
 * @param {ProjectEditRecord} pProject - the loaded Project record (must carry CreatingIDUser for the creator case)
 * @return {boolean}
 */
function canEditProject(pUser, pProject)
{
	if (isProjectAdministrator(pUser, pProject))
	{
		return true;
	}
	if (!pProject)
	{
		return false;
	}
	const tmpUser = pUser || {};
	const tmpLevel = Number(tmpUser.UserRoleIndex);
	const tmpUserID = Number(tmpUser.UserID);
	// Mine applies to levels 1-3 only: level 4 gets MyCustomer alone, so a level-4 creator in another customer is refused.
	return (tmpLevel >= 1) && (tmpLevel <= 3) && (tmpUserID > 0) && (Number(pProject.CreatingIDUser) === tmpUserID);
}

module.exports = { isProjectAdministrator, canEditProject };
